import styled, { createGlobalStyle } from 'styled-components';

/* ------------------------------------------------------------------ */
/* Design tokens                                                       */
/* ------------------------------------------------------------------ */
const c = {
    bg: '#05080D',
    surface: 'rgba(15, 23, 42, 0.55)',
    surfaceHover: 'rgba(20, 30, 52, 0.75)',
    border: 'rgba(148, 163, 184, 0.12)',
    borderStrong: 'rgba(148, 163, 184, 0.22)',
    accent: '#3b82f6',
    accentDeep: '#2563eb',
    accentSoft: '#93c5fd',
    cyan: '#22d3ee',
    text: '#f8fafc',
    textSoft: '#cbd5e1',
    muted: '#94a3b8',
    faint: '#64748b',
};

const ease = 'cubic-bezier(0.22, 1, 0.36, 1)';

/* Keeps the dark background when the page overscrolls / is taller than the viewport */
export const HomeGlobalStyle = createGlobalStyle`
    html, body {
        background: ${c.bg};
    }
`;

/* ------------------------------------------------------------------ */
/* Page shell                                                          */
/* ------------------------------------------------------------------ */
export const Page = styled.div`
    width: 100%;
    min-height: 100vh;
    position: relative;
    font-family: 'Outfit', -apple-system, BlinkMacSystemFont, sans-serif;
    color: #e2e8f0;
    background: ${c.bg};
    overflow-x: hidden;
    text-align: left;
`;

export const PageBackground = styled.div`
    position: fixed;
    inset: 0;
    z-index: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
`;

export const PageContent = styled.div`
    position: relative;
    z-index: 1;
`;

/* Soft glows placed behind the hero */
export const HeroGlow = styled.div`
    position: absolute;
    top: -120px;
    left: 50%;
    width: min(1100px, 140vw);
    height: 760px;
    transform: translateX(-50%);
    pointer-events: none;
    z-index: 0;
    background:
        radial-gradient(ellipse 45% 40% at 50% 38%, rgba(37, 99, 235, 0.28) 0%, transparent 70%),
        radial-gradient(ellipse 30% 30% at 68% 30%, rgba(34, 211, 238, 0.12) 0%, transparent 70%),
        radial-gradient(ellipse 30% 30% at 32% 45%, rgba(99, 102, 241, 0.14) 0%, transparent 70%);
    filter: blur(8px);
`;

/* ------------------------------------------------------------------ */
/* Top menu                                                            */
/* ------------------------------------------------------------------ */
export const TopMenu = styled.header`
    position: sticky;
    top: 0;
    z-index: 900;
    width: 100%;
    backdrop-filter: saturate(140%) blur(14px);
    -webkit-backdrop-filter: saturate(140%) blur(14px);
    background: rgba(5, 8, 13, 0.7);
    border-bottom: 1px solid ${c.border};
`;

export const TopMenuInner = styled.div`
    position: relative;
    max-width: 1200px;
    margin: 0 auto;
    padding: 12px 96px 12px 24px;
    min-height: 56px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 20px;

    @media (max-width: 560px) {
        padding-right: 80px;
    }
`;

export const TopMenuLogo = styled.img`
    height: 30px;
    width: auto;
    display: block;
`;

export const TopMenuBrand = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 6px 8px;
    border-radius: 12px;
    transition: opacity 0.2s ease;
    cursor: pointer;

    &:hover {
        opacity: 0.85;
    }
`;

export const TopMenuBrandText = styled.h5`
    margin: 0;
    font-family: 'McTenLowercaseAlt', sans-serif;
    font-size: 1.5rem;
    font-weight: 400;
    color: #ffffff;
    text-shadow: 0 0 12px rgba(147, 197, 253, 0.35);
    letter-spacing: 0.01em;
    line-height: 1;
    margin-bottom: 15px;

    @media (max-width: 520px) {
        font-size: 1.25rem;
    }
`;

export const TopMenuNav = styled.nav`
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
`;

export const TopMenuList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 4px;
    display: flex;
    align-items: center;
    gap: 2px;
    border-radius: 999px;
    border: 1px solid ${c.border};
    background: rgba(15, 23, 42, 0.45);

    @media (max-width: 860px) {
        display: none;
    }
`;

export const TopMenuItem = styled.li`
    position: relative;

    &:hover > div,
    &:focus-within > div {
        opacity: 1;
        visibility: visible;
        transform: translateY(0);
    }
`;

export const TopMenuLink = styled.a`
    color: ${c.textSoft};
    text-decoration: none;
    font-size: 0.94rem;
    font-weight: 500;
    padding: 8px 14px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: color 0.2s ease, background 0.2s ease;
    white-space: nowrap;

    &:hover,
    &:focus-visible {
        color: ${c.text};
        background: rgba(148, 163, 184, 0.1);
        outline: none;
    }
`;

export const TopMenuArrow = styled.span`
    font-size: 0.7rem;
    opacity: 0.7;
    transform: translateY(1px);
`;

export const TopMenuDropdown = styled.div`
    position: absolute;
    top: calc(100% + 12px);
    left: 50%;
    margin-left: -130px;
    min-width: 260px;
    padding: 8px;
    border-radius: 14px;
    border: 1px solid ${c.borderStrong};
    background: rgba(8, 12, 20, 0.96);
    box-shadow: 0 24px 48px -12px rgba(0, 0, 0, 0.6);
    opacity: 0;
    visibility: hidden;
    transform: translateY(6px);
    transition: opacity 0.2s ease, transform 0.2s ${ease}, visibility 0.2s ease;
    backdrop-filter: blur(10px);

    /* hover bridge so the dropdown doesn't close in the gap */
    &::before {
        content: '';
        position: absolute;
        top: -14px;
        left: 0;
        right: 0;
        height: 14px;
    }
`;

export const TopMenuDropdownLink = styled.a`
    display: block;
    color: ${c.textSoft};
    text-decoration: none;
    font-size: 0.93rem;
    padding: 10px 12px;
    border-radius: 9px;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover,
    &:focus-visible {
        background: rgba(59, 130, 246, 0.12);
        color: ${c.text};
        outline: none;
    }
`;

export const AuthButtonWrapper = styled.div`
    position: fixed;
    top: 9px;
    right: 24px;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;

    @media (max-width: 560px) {
        right: 16px;
    }
`;

export const AuthButton = styled.button`
    width: 44px;
    height: 44px;
    padding: 0;
    border-radius: 50%;
    border: 1px solid ${c.borderStrong};
    background: ${(p) => (p.$loggedIn ? c.accentDeep : 'rgba(30, 41, 59, 0.9)')};
    color: ${(p) => (p.$loggedIn ? '#fff' : '#e2e8f0')};
    font-family: 'Outfit', sans-serif;
    font-size: 1.05rem;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s ${ease}, box-shadow 0.2s ease, border-color 0.2s ease;
    overflow: hidden;

    &:hover {
        transform: scale(1.05);
        border-color: rgba(147, 197, 253, 0.5);
        box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.15);
    }
`;

/* ------------------------------------------------------------------ */
/* Hero                                                                */
/* ------------------------------------------------------------------ */
export const Hero = styled.section`
    position: relative;
    padding: 88px 24px 72px;
    text-align: center;
    max-width: 1040px;
    margin: 0 auto;

    & > *:not(${HeroGlow}) {
        position: relative;
        z-index: 1;
    }

    & [data-animate] {
        opacity: 0;
        transform: translateY(20px);
    }

    @keyframes ghostFloat {
        0%,
        100% {
            transform: translateY(0);
        }
        50% {
            transform: translateY(-10px);
        }
    }

    .hero-logo-ghost {
        display: flex;
        justify-content: center;
        align-items: center;
        animation: ghostFloat 4s ease-in-out infinite;
        will-change: transform;
        filter: drop-shadow(0 12px 40px rgba(59, 130, 246, 0.35));
    }

    .hero-logo-ghost img {
        width: 112px;
        height: 112px;
        object-fit: contain;
    }

    @media (max-width: 640px) {
        padding: 56px 20px 56px;

        .hero-logo-ghost img {
            width: 88px;
            height: 88px;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .hero-logo-ghost {
            animation: none;
        }
    }
`;

export const HeroBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 7px 16px 7px 12px;
    background: rgba(37, 99, 235, 0.1);
    border: 1px solid rgba(59, 130, 246, 0.3);
    color: ${c.accentSoft};
    font-size: 0.82rem;
    font-weight: 500;
    border-radius: 999px;
    margin: 28px 0 28px;
    letter-spacing: 0.01em;
    max-width: 100%;

    .dot {
        position: relative;
        flex-shrink: 0;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: ${c.cyan};
        box-shadow: 0 0 10px ${c.cyan};
    }

    .dot::after {
        content: '';
        position: absolute;
        inset: -4px;
        border-radius: 50%;
        border: 1px solid ${c.cyan};
        opacity: 0;
        animation: badgePulse 2.4s ease-out infinite;
    }

    @keyframes badgePulse {
        0% {
            transform: scale(0.6);
            opacity: 0.8;
        }
        100% {
            transform: scale(1.8);
            opacity: 0;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .dot::after {
            animation: none;
        }
    }
`;

export const HeroTitle = styled.h1`
    font-size: clamp(2.3rem, 5.6vw, 4.1rem);
    font-weight: 700;
    line-height: 1.06;
    margin: 0 auto 24px;
    max-width: 920px;
    color: ${c.text};
    letter-spacing: -0.035em;
    text-wrap: balance;

    .accent {
        display: block;
        background: linear-gradient(90deg, #93c5fd 0%, #60a5fa 35%, #22d3ee 100%);
        -webkit-background-clip: text;
        background-clip: text;
        color: transparent;
        padding-bottom: 0.06em;
    }
`;

export const HeroSubtitle = styled.p`
    font-size: clamp(1.05rem, 1.8vw, 1.22rem);
    line-height: 1.65;
    color: ${c.muted};
    margin: 0 auto 36px;
    max-width: 700px;
    text-wrap: pretty;
`;

export const BadgeRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px 22px;
    margin-bottom: 40px;
`;

export const Badge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 0.92rem;
    font-weight: 500;
    color: ${c.textSoft};

    svg {
        flex-shrink: 0;
        width: 18px;
        height: 18px;
        padding: 3px;
        border-radius: 50%;
        color: ${c.cyan};
        background: rgba(34, 211, 238, 0.12);
    }
`;

export const CTAWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-bottom: 18px;
`;

const buttonBase = `
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 14px 24px;
    font-size: 1rem;
    font-weight: 600;
    border-radius: 12px;
    text-decoration: none;
    transition: transform 0.2s ${ease}, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease;

    svg {
        width: 18px;
        height: 18px;
        transition: transform 0.2s ${ease};
    }

    &:hover svg {
        transform: translateX(3px);
    }

    &:focus-visible {
        outline: 2px solid ${c.accentSoft};
        outline-offset: 3px;
    }
`;

export const CTAPrimary = styled.a`
    ${buttonBase}
    color: #fff;
    background: linear-gradient(180deg, #3b82f6 0%, #2563eb 100%);
    box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.2),
        0 0 0 1px rgba(37, 99, 235, 0.7),
        0 10px 30px -8px rgba(37, 99, 235, 0.7);

    &:hover {
        transform: translateY(-2px);
        box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.25),
            0 0 0 1px rgba(59, 130, 246, 0.9),
            0 16px 40px -8px rgba(37, 99, 235, 0.8);
    }
`;

export const CTASecondary = styled.a`
    ${buttonBase}
    color: #e2e8f0;
    background: rgba(15, 23, 42, 0.6);
    border: 1px solid ${c.borderStrong};

    &:hover {
        background: rgba(30, 41, 59, 0.8);
        border-color: rgba(148, 163, 184, 0.4);
    }
`;

export const HeroNote = styled.p`
    font-size: 0.88rem;
    color: ${c.faint};
    margin: 0;
`;

/* ------------------------------------------------------------------ */
/* Sections                                                            */
/* ------------------------------------------------------------------ */
export const Section = styled.section`
    padding: 88px 24px;
    max-width: 1200px;
    margin: 0 auto;
    ${(p) => p.$flushTop && 'padding-top: 8px !important;'}

    & [data-animate] {
        opacity: 0;
        transform: translateY(24px);
    }

    @media (max-width: 640px) {
        padding: 64px 20px;
    }
`;

export const SectionEyebrow = styled.span`
    display: block;
    text-align: center;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${c.accentSoft};
    margin-bottom: 14px;
`;

export const SectionTitle = styled.h2`
    font-size: clamp(1.8rem, 3.4vw, 2.6rem);
    font-weight: 700;
    line-height: 1.15;
    text-align: center;
    margin: 0 auto 14px;
    max-width: 780px;
    color: ${c.text};
    letter-spacing: -0.03em;
    text-wrap: balance;
`;

export const SectionSubtitle = styled.p`
    font-size: 1.08rem;
    line-height: 1.6;
    color: ${c.muted};
    text-align: center;
    margin: 0 auto 52px;
    max-width: 620px;
    text-wrap: pretty;
`;

/* Shared card look */
const cardBase = `
    position: relative;
    background: linear-gradient(180deg, rgba(20, 29, 48, 0.72) 0%, rgba(11, 17, 30, 0.72) 100%);
    border: 1px solid ${c.border};
    border-radius: 16px;
    text-align: left;
    transition: border-color 0.25s ease, transform 0.25s ${ease}, box-shadow 0.25s ease, background 0.25s ease;

    &:hover {
        border-color: rgba(59, 130, 246, 0.35);
        transform: translateY(-3px);
        box-shadow: 0 18px 40px -18px rgba(37, 99, 235, 0.45);
    }

    h3 {
        font-size: 1.1rem;
        font-weight: 600;
        margin: 0 0 8px 0;
        color: #f1f5f9;
        letter-spacing: -0.01em;
    }

    p {
        font-size: 0.95rem;
        color: ${c.muted};
        line-height: 1.6;
        margin: 0;
    }
`;

export const IconTile = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: 12px;
    margin-bottom: 18px;
    color: ${c.accentSoft};
    background: linear-gradient(180deg, rgba(59, 130, 246, 0.2) 0%, rgba(59, 130, 246, 0.06) 100%);
    border: 1px solid rgba(59, 130, 246, 0.28);

    svg {
        width: 20px;
        height: 20px;
    }
`;

/* Chat demo framed as an app window */
export const DemoFrame = styled.div`
    position: relative;
    max-width: 980px;
    margin: 0 auto;

    &::before {
        content: '';
        position: absolute;
        inset: 8% 6% -4%;
        background: radial-gradient(ellipse at center, rgba(37, 99, 235, 0.35) 0%, transparent 70%);
        filter: blur(40px);
        z-index: -1;
    }
`;

export const DemoWindow = styled.div`
    border-radius: 20px;
    border: 1px solid ${c.borderStrong};
    background: rgba(8, 12, 20, 0.85);
    box-shadow: 0 40px 80px -30px rgba(0, 0, 0, 0.8);
    overflow: hidden;

    & > div:last-child {
        border: none;
        border-radius: 0;
        box-shadow: none;
    }
`;

export const DemoBar = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    border-bottom: 1px solid ${c.border};
    background: rgba(15, 23, 42, 0.8);
    font-size: 0.82rem;
    color: ${c.faint};

    i {
        width: 11px;
        height: 11px;
        border-radius: 50%;
        background: #334155;
    }

    i:nth-of-type(1) {
        background: #f87171;
    }
    i:nth-of-type(2) {
        background: #fbbf24;
    }
    i:nth-of-type(3) {
        background: #34d399;
    }

    span {
        flex: 1;
        text-align: center;
        margin-right: 49px;
    }

    @media (max-width: 480px) {
        span {
            margin-right: 0;
            text-align: right;
        }
    }
`;

export const FeatureGrid = styled.div`
    display: grid;
    margin-top: 44px;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;

    @media (max-width: 980px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 560px) {
        grid-template-columns: 1fr;
    }
`;

export const FeatureCard = styled.div`
    ${cardBase}
    padding: 26px;
`;

export const ArchitectureGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;

    @media (max-width: 980px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 560px) {
        grid-template-columns: 1fr;
    }
`;

export const ArchitectureCard = styled.div`
    ${cardBase}
    padding: 26px;
    overflow: hidden;

    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 24px;
        right: 24px;
        height: 1px;
        background: linear-gradient(90deg, transparent, rgba(147, 197, 253, 0.6), transparent);
        opacity: 0;
        transition: opacity 0.25s ease;
    }

    &:hover::after {
        opacity: 1;
    }

    .arch-label {
        display: block;
        font-size: 0.72rem;
        font-weight: 600;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: ${c.faint};
        margin-bottom: 6px;
    }
`;

/* Steps timeline */
export const StepsSection = styled.section`
    position: relative;
    padding: 96px 24px;
    border-top: 1px solid ${c.border};
    border-bottom: 1px solid ${c.border};
    background:
        radial-gradient(ellipse 60% 80% at 50% 0%, rgba(37, 99, 235, 0.1) 0%, transparent 70%),
        rgba(8, 12, 20, 0.6);

    & [data-animate] {
        opacity: 0;
        transform: translateY(24px);
    }

    @media (max-width: 640px) {
        padding: 64px 20px;
    }
`;

export const StepsInner = styled.div`
    max-width: 1100px;
    margin: 0 auto;
`;

export const StepList = styled.ol`
    list-style: none;
    padding: 0;
    margin: 0;
    counter-reset: step;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    position: relative;

    /* connecting line between step numbers */
    &::before {
        content: '';
        position: absolute;
        top: 20px;
        left: 12.5%;
        right: 12.5%;
        height: 1px;
        background: linear-gradient(90deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.6), rgba(59, 130, 246, 0.1));
    }

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
        gap: 0;

        &::before {
            top: 20px;
            bottom: 20px;
            left: 20px;
            right: auto;
            width: 1px;
            height: auto;
            background: linear-gradient(180deg, rgba(59, 130, 246, 0.6), rgba(59, 130, 246, 0.1));
        }
    }
`;

export const StepItem = styled.li`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    &::before {
        counter-increment: step;
        content: '0' counter(step);
        position: relative;
        z-index: 1;
        flex-shrink: 0;
        width: 40px;
        height: 40px;
        margin-bottom: 20px;
        background: ${c.bg};
        color: ${c.accentSoft};
        border: 1px solid rgba(59, 130, 246, 0.55);
        box-shadow: 0 0 0 6px rgba(5, 8, 13, 1), 0 0 24px rgba(59, 130, 246, 0.35);
        font-weight: 600;
        font-size: 0.9rem;
        font-variant-numeric: tabular-nums;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    & > div {
        padding: 0 8px;
    }

    h3 {
        font-size: 1.1rem;
        font-weight: 600;
        margin: 0 0 8px 0;
        color: #f1f5f9;
    }

    p {
        font-size: 0.94rem;
        color: ${c.muted};
        line-height: 1.6;
        margin: 0;
    }

    @media (max-width: 900px) {
        flex-direction: row;
        align-items: flex-start;
        text-align: left;
        gap: 20px;
        padding-bottom: 28px;

        &::before {
            margin-bottom: 0;
        }

        & > div {
            padding: 6px 0 0;
        }
    }
`;

/* Sector use cases */
export const UseCaseGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

export const UseCaseCard = styled.div`
    ${cardBase}
    padding: 28px;
    display: flex;
    flex-direction: column;

    p {
        margin-bottom: 20px;
    }

    a {
        margin-top: auto;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        color: ${c.accentSoft};
        font-size: 0.92rem;
        font-weight: 600;
        text-decoration: none;
    }

    a svg {
        width: 16px;
        height: 16px;
        transition: transform 0.2s ${ease};
    }

    a:hover {
        color: #bfdbfe;
    }

    a:hover svg {
        transform: translateX(3px);
    }

    /* whole card clickable */
    a::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: inherit;
    }
`;

/* Agents */
export const AgentLinkGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 560px) {
        grid-template-columns: 1fr;
    }
`;

export const AgentLinkCard = styled.a`
    ${cardBase}
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 20px 22px;
    text-decoration: none;

    ${IconTile} {
        flex-shrink: 0;
        margin-bottom: 0;
        width: 40px;
        height: 40px;
    }

    .agent-body {
        flex: 1;
        min-width: 0;
    }

    strong {
        display: block;
        color: #f1f5f9;
        font-size: 1rem;
        font-weight: 600;
        margin-bottom: 4px;
    }

    span.agent-desc {
        display: block;
        color: ${c.muted};
        font-size: 0.9rem;
        line-height: 1.5;
    }

    .agent-arrow {
        flex-shrink: 0;
        width: 18px;
        height: 18px;
        margin-top: 2px;
        color: ${c.faint};
        transition: transform 0.2s ${ease}, color 0.2s ease;
    }

    &:hover .agent-arrow {
        color: ${c.accentSoft};
        transform: translateX(3px);
    }

    &:focus-visible {
        outline: 2px solid ${c.accentSoft};
        outline-offset: 3px;
    }

    ${(p) =>
        p.$featured &&
        `
        border-color: rgba(59, 130, 246, 0.4);
        background: linear-gradient(180deg, rgba(30, 58, 138, 0.35) 0%, rgba(11, 17, 30, 0.8) 100%);
    `}
`;

/* Logo carousel with faded edges */
export const LogoStrip = styled.div`
    position: relative;
    padding: 12px 0;
    border-radius: 20px;
    border: 1px solid ${c.border};
    background: rgba(248, 250, 252, 0.03);
    -webkit-mask-image: linear-gradient(90deg, transparent 0%, #000 12%, #000 88%, transparent 100%);
    mask-image: linear-gradient(90deg, transparent 0%, #000 12%, #000 88%, transparent 100%);

    & > div {
        margin: 0 !important;
    }
`;

/* ------------------------------------------------------------------ */
/* Footer                                                              */
/* ------------------------------------------------------------------ */
export const FooterCTA = styled.section`
    position: relative;
    max-width: 1100px;
    margin: 24px auto 96px;
    padding: 72px 32px;
    color: #fff;
    text-align: center;
    border-radius: 28px;
    border: 1px solid rgba(59, 130, 246, 0.3);
    overflow: hidden;
    background:
        radial-gradient(ellipse 70% 90% at 50% 0%, rgba(37, 99, 235, 0.35) 0%, transparent 70%),
        radial-gradient(ellipse 40% 60% at 90% 100%, rgba(34, 211, 238, 0.12) 0%, transparent 70%),
        linear-gradient(180deg, #0b1324 0%, #070b14 100%);
    box-shadow: 0 40px 80px -40px rgba(37, 99, 235, 0.5);

    & [data-animate] {
        opacity: 0;
        transform: translateY(20px);
    }

    @media (max-width: 1148px) {
        margin-left: 24px;
        margin-right: 24px;
    }

    @media (max-width: 640px) {
        margin: 8px 16px 64px;
        padding: 56px 22px;
        border-radius: 22px;
    }
`;

export const FooterCTATitle = styled.h2`
    font-size: clamp(1.8rem, 3.6vw, 2.75rem);
    font-weight: 700;
    line-height: 1.15;
    margin: 0 auto 18px;
    max-width: 720px;
    letter-spacing: -0.03em;
    text-wrap: balance;
`;

export const FooterCTASubtitle = styled.p`
    font-size: 1.08rem;
    line-height: 1.6;
    color: ${c.muted};
    margin: 0 auto 36px;
    max-width: 640px;
    text-wrap: pretty;
`;

export const FooterCTAButton = styled.a`
    ${buttonBase}
    padding: 15px 28px;
    background: #fff;
    color: #0b1324;
    box-shadow: 0 10px 30px -10px rgba(255, 255, 255, 0.35);

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 16px 40px -10px rgba(255, 255, 255, 0.45);
    }
`;

export const FooterBottom = styled.footer`
    padding: 28px 24px 36px;
    text-align: center;
    font-size: 0.88rem;
    color: ${c.faint};
    border-top: 1px solid ${c.border};
`;
